<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { onMount, onDestroy, untrack } from 'svelte';
  import { navigate, Link } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import { clockAt } from '../lib/utils/checkin';
  import type { Activity, Fix, LiveActivity, LiveJoined, MapInfo, MemberLocation, NotificationItem, Place, RefreshResult, CheckIn, SosAlert } from '../lib/api/types';
  import SosButton from '../lib/components/SosButton.svelte';
  import ShareLinks from '../lib/components/ShareLinks.svelte';
  import { activeAt, nextEnd, type ShareLink } from '../lib/utils/sharelinks';
  import { activityIcon, activityLabel, clockTime, length, rate } from '../lib/utils/activities';
  import { usesMiles } from '../lib/stores/units.svelte';
  import { sportOf } from '../lib/utils/sports';
  import { groups } from '../lib/stores/groups';
  import { isAuthenticated } from '../lib/stores/auth';
  import { websocket } from '../lib/stores/websocket';
  import { age, clock } from '../lib/utils/time';
  import { circlePolygon, initials } from '../lib/utils/geo';
  import { doing, memberState, type MemberState } from '../lib/utils/memberState';
  import { fitPins } from '../lib/utils/pinFit';
  import { groupLabel } from '../lib/utils/groupLabel';
  import { joinPins } from '../lib/utils/joinPins';
  import { iconClass } from '../lib/utils/icons';
  import { photoUrl } from '../lib/utils/photos';
  import { auth } from '../lib/stores/auth';
  import Avatar from '../lib/components/Avatar.svelte';
  import MemberPanel from '../lib/components/map/MemberPanel.svelte';
  import type { DayCard } from '../lib/components/map/dayCards';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import { palette } from '../lib/utils/palette';
  import { watchSight } from '../lib/utils/sight';
  import { pinText, shownDestination } from '../lib/utils/eta';
  import { integer, t } from '../lib/i18n/index.ts';

  // filter is the group the map shows, or '' for everyone the viewer shares
  // any group with. The map opens on the group last picked in this browser,
  // and on everyone once that group is gone (spec 001 FR-038). A tab
  // managing a child neither reads nor writes the parent's choice.
  const FILTER_KEY = 'map_group';
  const rememberFilter = !api.acting;
  let filter = $state(rememberFilter ? localStorage.getItem(FILTER_KEY) ?? '' : '');
  const groupsLoaded = groups.loaded;
  $effect(() => {
    if ($groupsLoaded && filter && !$groups.some((g) => g.id === filter)) filter = '';
  });
  $effect(() => {
    if (!rememberFilter) return;
    if (filter) localStorage.setItem(FILTER_KEY, filter);
    else localStorage.removeItem(FILTER_KEY);
  });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // stateOf describes a member as every client does (spec 010 FR-009).
  // miles is whether distance and speed show in miles (010 FR-034).
  let miles = $derived(usesMiles());
  const stateOf = (m: MemberLocation): MemberState => memberState(m, now, tz, miles);
  // The selected member's day on the map: their trips, the one picked, and
  // where they left their last stop (010 FR-006).
  let dayCards: DayCard[] = $state([]);
  let picked: DayCard | null = $state(null);
  let leftLabel: maplibregl.Marker | null = null;

  let mapEl: HTMLDivElement | undefined = $state();
  let viewEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  // A pin is 48 px wide, its tail tip 24 px in from its left edge (28 px on
  // a selected pin, which is 56 px); pins closer than that join (010 FR-004).
  const PIN_SIZE = 48;
  const PIN_LEFT = -24;
  const PIN_LEFT_SELECTED = -28;
  // A joined pin's photos are STACK_PHOTO px, each STACK_STEP px past the
  // last, and STACK_MAX show before "+N", as the apps' are.
  const STACK_PHOTO = 40;
  const STACK_STEP = 24;
  const STACK_MAX = 3;
  let mapReady = $state(false);
  let mapError = $state('');
  // everyone is each person the viewer shares a group with, once; members
  // are those the filter shows.
  let everyone: MemberLocation[] = $state([]);
  const members = $derived(filter ? everyone.filter((m) => m.groups?.includes(filter)) : everyone);
  let allPlaces: Place[] = $state([]);
  let stopTheme: (() => void) | null = null;
  let loadError = $state('');
  let now = $state(Date.now());
  let focused: string | null = $state(null);
  const markers = new Map<string, maplibregl.Marker>();
  // joined holds the joined pins on the map, by their members' IDs.
  const joined = new Map<string, maplibregl.Marker>();
  // flags marks the destinations whose ETAs show, by destination ID (spec 003 FR-024).
  const flags = new Map<string, maplibregl.Marker>();
  let unsubscribeTopics: (() => void)[] = [];
  let fitted = false;
  let refreshNote = $state('');
  // crashAlerts are crash alerts from the last day, since the web gets no
  // push and a recipient may be looking at the map instead.
  let crashAlerts: { ref: string; title: string }[] = $state([]);
  // watching are check-in timers that name the viewer, and mine is the
  // viewer's own (spec 004 FR-029); both are read every 30 seconds.
  let watching: CheckIn[] = $state([]);
  let mine: CheckIn | null = $state(null);
  // sosPaged are open SOS alerts that paged the viewer, and sosMine is the
  // viewer's own open SOS (spec 004), read with the timers.
  let sosPaged: SosAlert[] = $state([]);
  let sosMine: SosAlert | null = $state(null);
  function loadCheckIns() {
    api.get<CheckIn[]>('/check-ins/watching').then((l) => (watching = l)).catch(() => {});
    api.get<{ current: CheckIn | null }>('/me/check-in').then((c) => (mine = c.current)).catch(() => {});
    api.get<SosAlert[]>('/sos/watching').then((l) => (sosPaged = l)).catch(() => {});
    api.get<{ current: SosAlert | null }>('/me/sos').then((c) => (sosMine = c.current)).catch(() => {});
  }
  loadCheckIns();
  const checkInPoll = setInterval(loadCheckIns, 30_000);
  onDestroy(() => clearInterval(checkInPoll));

  // shareLinks are the viewer's own active share links, which the map says
  // are sharing their location (spec 001 FR-114). They are read when the map
  // opens, when the server says they changed, and on reconnecting, and each
  // is dropped at its end, which the server sends nothing for. A parent
  // acting for a child has none to show: a child makes no links.
  let shareLinks: ShareLink[] = $state([]);
  let shareOpen = $state(false);
  let shareEnd: ReturnType<typeof setTimeout> | undefined;
  function keepShareLinks(links: ShareLink[]) {
    clearTimeout(shareEnd);
    shareLinks = activeAt(links, Date.now());
    const end = nextEnd(shareLinks);
    // A day is the longest a link lasts, so the wait fits a timer.
    if (end !== null) shareEnd = setTimeout(() => keepShareLinks(shareLinks), Math.max(end - Date.now(), 0) + 500);
  }
  function loadShareLinks() {
    if (api.acting) return;
    api.get<ShareLink[]>('/me/share-links').then(keepShareLinks).catch(() => {});
  }
  function stopShareLink(l: ShareLink) {
    api.delete(`/me/share-links/${l.id}`).catch(() => {}).finally(loadShareLinks);
  }
  loadShareLinks();
  onDestroy(() => clearTimeout(shareEnd));
  // The viewer's own topic is held here and not with the groups', since a
  // member in no group can still hold a link.
  $effect(() => {
    const me = $auth.user?.id;
    if (!me || api.acting) return;
    const stops = [
      websocket.subscribeTopic(`viewer:${me}`, (_payload, topic, type) => {
        if (topic === `viewer:${me}` && type === 'share_links') loadShareLinks();
      }),
      websocket.onReconnect(loadShareLinks),
    ];
    return () => stops.forEach((u) => u());
  });

  // Follow is the live session the viewer is watching, with the trail the
  // member's points have drawn since it started.
  interface Follow {
    userId: string;
    sessionId: string;
    topic: string;
    state: string;
    reason?: string;
    // near is the road or place the member is near (002 FR-020).
    near?: string;
    trail: { at: number; lon: number; lat: number }[];
  }
  let follow: Follow | null = $state(null);
  let followError = $state('');
  let unsubscribeLive: (() => void) | null = null;
  let rejoinTimer: ReturnType<typeof setInterval> | null = null;
  // centering keeps the followed member in the middle of the map until the
  // viewer drags it; selecting them again centers them again (002 FR-017).
  let centering = true;
  // awayFrom is who was followed when the tab went out of sight, to follow
  // again when it comes back with them still selected (002 FR-015).
  let awayFrom: string | null = null;
  const stopWatchingSight = watchSight(
    () => {
      if (!follow) return;
      awayFrom = follow.userId;
      stopFollow();
    },
    () => {
      const userId = awayFrom;
      awayFrom = null;
      if (userId && focused === userId) startFollow(userId);
    },
  );

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  // Ages tick without a reload.
  const ticker = setInterval(() => (now = Date.now()), 1_000);

  onMount(async () => {
    api.get<NotificationItem[]>('/me/notifications?recent=true').then((list) => {
      const day = Date.now() - 24 * 3600 * 1000;
      const resolved = new Set(list.filter((n) => n.kind === 'crash_ok').map((n) => n.message.ref));
      crashAlerts = list
        .filter((n) => n.kind === 'crash' && n.message.ref && new Date(n.created_at).getTime() > day && !resolved.has(n.message.ref))
        .map((n) => ({ ref: n.message.ref!, title: n.message.title ?? t('mapCrashAlertFallback') }));
    }).catch(() => {});
    try {
      await groups.load();
    } catch (e: any) {
      loadError = e.message;
    }
    let info: MapInfo | null = null;
    try {
      info = await api.get<MapInfo>('/map');
    } catch (e: any) {
      mapError = e.message;
    }
    if (!mapEl) return;
    map = northUpMap({
      container: mapEl,
      style: styleFor(info),
      center: [-98.5, 39.8],
      zoom: 3,
      attributionControl: false,
    });
    // The proxied style's sources carry the OpenStreetMap attribution; the
    // fallback background has none, so it gets the server's.
    addCredits(map, info, 'main');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // The view buttons sit under the zoom buttons as one more control (spec 010 FR-008).
    if (viewEl) map.addControl({ onAdd: () => viewEl!, onRemove: () => {} }, 'top-right');
    // Only a drag by the viewer stops centering; the map's own moves have no
    // originalEvent.
    map.on('dragstart', (e) => {
      if (e.originalEvent) centering = false;
    });
    if (!info?.available) {
      mapError = t('mapTilesOff');
    }
    // Pins join by where they fall on screen, so the join reruns as the map
    // moves; each tip stays on its spot through zoom and rotation (010 FR-004).
    map.on('move', joinUp);
    map.on('movestart', closeChoices);
    map.on('click', closeChoices);
    stopTheme = followTheme(map, info, () => {
      const blue = palette('blue');
      const peach = palette('peach');
      const gap = palette('overlay2');
      map!.addSource('accuracy', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'accuracy-fill', type: 'fill', source: 'accuracy', paint: { 'fill-color': blue, 'fill-opacity': 0.12 } });
      map!.addLayer({ id: 'accuracy-line', type: 'line', source: 'accuracy', paint: { 'line-color': blue, 'line-opacity': 0.4, 'line-width': 1 } });
      map!.addSource('activities', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addSource('activity-gaps', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'activity-gaps', type: 'line', source: 'activity-gaps', paint: { 'line-color': gap, 'line-width': 2, 'line-dasharray': [2, 2] } });
      map!.addLayer({ id: 'activities', type: 'line', source: 'activities', paint: { 'line-color': peach, 'line-width': 4, 'line-opacity': 0.9 } });
      map!.addSource('day', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'day', type: 'line', source: 'day', paint: { 'line-color': blue, 'line-width': 4, 'line-opacity': 0.6 } });
      map!.addSource('picked', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'picked', type: 'line', source: 'picked', paint: { 'line-color': peach, 'line-width': 6 } });
      // Routes to destinations draw under trails and members (spec 003 FR-024).
      map!.addSource('eta-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'eta-routes-casing', type: 'line', source: 'eta-routes', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': palette('base'), 'line-width': 8, 'line-opacity': 0.9 } });
      map!.addLayer({ id: 'eta-routes', type: 'line', source: 'eta-routes', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': palette('mauve'), 'line-width': 5 } });
      map!.addSource('trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'trail', type: 'line', source: 'trail', paint: { 'line-color': blue, 'line-width': 4, 'line-opacity': 0.8 } });
      mapReady = true;
      render();
      drawDay();
    });
  });

  onDestroy(() => {
    stopTheme?.();
    stopWatchingSight();
    stopFollow();
    clearInterval(ticker);
    unsubscribeTopics.forEach((u) => u());
    map?.remove();
  });

  // groupOf is the group requests about a member go through: the filter, or
  // else the first group the viewer shares with them. What the viewer sees is
  // the same through any of them (spec 001 FR-037).
  function groupOf(userId: string): string {
    const m = everyone.find((x) => x.user_id === userId);
    return filter || m?.groups?.[0] || $groups[0]?.id || '';
  }

  function loadEveryone() {
    api.get<MemberLocation[]>('/locations')
      .then((list) => { everyone = list; syncRoutes(); render(); })
      .catch((e: any) => (loadError = e.message));
  }

  // Load everyone and every group's places, and listen, whenever the
  // viewer's groups change: locations and activities come on the viewer's
  // own topic, since what each viewer sees differs, and places and member
  // changes on each group's.
  $effect(() => {
    const list = $groups;
    const me = $auth.user?.id;
    unsubscribeTopics.forEach((u) => u());
    unsubscribeTopics = [];
    everyone = [];
    allPlaces = [];
    fitted = false;
    untrack(() => stopFollow());
    if (list.length === 0 || !me) return;
    routes = {};
    routeIds = {};
    loadEveryone();
    for (const g of list) {
      loadPlaces(g.id);
      refresh(g.id);
      unsubscribeTopics.push(websocket.subscribeTopic(`group:${g.id}`, (_payload, topic, type) => {
        if (topic !== `group:${g.id}`) return;
        if (type === 'members' || type === 'photo') {
          // A changed photo shows within a refresh, as 005 FR-013 asks, without
          // waiting for the member's next upload.
          loadEveryone();
        } else if (type === 'places') {
          loadPlaces(g.id);
        }
      }));
    }
    // Locations and member changes sent while the socket was down are lost,
    // so they are read again when it comes back (001 FR-113).
    unsubscribeTopics.push(websocket.onReconnect(() => {
      loadEveryone();
      for (const g of list) loadPlaces(g.id);
    }));
    unsubscribeTopics.push(websocket.subscribeTopic(`viewer:${me}`, (payload, topic, type) => {
      if (topic !== `viewer:${me}`) return;
      if (type === 'location' || type === 'status') {
        const p = payload as MemberLocation;
        const i = everyone.findIndex((m) => m.user_id === p.user_id);
        if (i >= 0) everyone[i] = p;
        else everyone = [...everyone, p];
        // A recording member's route is read again with each upload: a
        // location message carries only the batch's latest fix.
        if (routes[p.user_id] && p.activity?.state === 'recording') loadRoute(p.user_id, p.activity.id);
        syncRoutes();
        render();
        // The viewer's own pin is never followed live, so while it is
        // selected their own locations keep it centered (002 FR-017).
        if (type === 'location' && p.user_id === me && focused === me && centering && p.location) {
          map?.easeTo({ center: [p.location.lon, p.location.lat], duration: 500 });
        }
      } else if (type === 'activity') {
        const a = payload as LiveActivity;
        const i = everyone.findIndex((m) => m.user_id === a.user_id);
        if (i >= 0) everyone[i] = { ...everyone[i], activity: a.state === 'finished' ? undefined : a };
        syncRoutes();
        render();
      }
    }));
  });

  // Choosing a group frames its members; one not in it is closed.
  $effect(() => {
    void filter;
    untrack(() => {
      if (focused && !members.some((m) => m.user_id === focused)) unfocus();
      fitted = false;
      render();
    });
  });

  // refresh asks phones whose locations are over two minutes old, or 15
  // seconds for a member recording an activity, to report now (002 FR-011,
  // specs/006-activities/research.md); their answers arrive as location messages.
  async function refresh(groupId: string) {
    try {
      const res = await api.post<RefreshResult>(`/groups/${groupId}/refresh`);
      refreshNote = res.asked.length > 0 ? t('mapAskingPhones', { n: res.asked.length }) : '';
      if (refreshNote) setTimeout(() => (refreshNote = ''), 15_000);
    } catch {
      // The map still shows each member's last location with its age.
    }
  }

  async function startFollow(userId: string) {
    const groupId = groupOf(userId);
    if (!groupId) return;
    await stopFollow();
    followError = '';
    centering = true;
    try {
      const j = await api.post<LiveJoined>(`/groups/${groupId}/members/${userId}/live`);
      follow = { userId, sessionId: j.session_id, topic: j.topic, state: j.state, reason: j.reason, near: j.near, trail: [] };
      unsubscribeLive = websocket.subscribeTopic(j.topic, onLive);
      // Joining again keeps this viewer in the session across socket
      // reconnects, which count as leaving.
      rejoinTimer = setInterval(() => rejoin(groupId, userId), 60_000);
    } catch (e: any) {
      followError = e.message;
    }
  }

  async function rejoin(groupId: string, userId: string) {
    if (!follow) return;
    try {
      const j = await api.post<LiveJoined>(`/groups/${groupId}/members/${userId}/live`);
      if (!follow) return;
      // A socket that reconnected tried the topic while this viewer counted
      // as gone and was refused, so the topic is subscribed again each time;
      // the session may also be a new one if the old ended meanwhile.
      unsubscribeLive?.();
      if (j.session_id !== follow.sessionId) follow = { ...follow, sessionId: j.session_id, topic: j.topic, state: j.state };
      unsubscribeLive = websocket.subscribeTopic(j.topic, onLive);
    } catch {
      // The next attempt settles it.
    }
  }

  // A reconnected socket joins the session again at once rather than at the
  // next minute's join, so live points resume (002 FR-014).
  let wasConnected = false;
  $effect(() => {
    const connected = $websocket.connected;
    if (connected && !wasConnected && follow) rejoin(groupOf(follow.userId), follow.userId);
    wasConnected = connected;
  });

  function onLive(payload: unknown, _topic: string, type: string) {
    if (!follow) return;
    if (type === 'point') {
      const fix = payload as Fix;
      const i = everyone.findIndex((m) => m.user_id === follow!.userId);
      if (i >= 0) everyone[i] = { ...everyone[i], location: fix, status: 'current', reason: undefined };
      // A watched activity already draws its route.
      if (!everyone[i]?.activity) follow.trail = [...follow.trail, { at: Date.parse(fix.recorded_at), lon: fix.lon, lat: fix.lat }];
      render();
      if (centering) map?.easeTo({ center: [fix.lon, fix.lat], duration: 500 });
    } else if (type === 'state') {
      const st = payload as { state: string; reason?: string };
      follow.state = st.state;
      follow.reason = st.reason;
      if (st.state === 'ended') {
        followError = t('mapFollowEnded');
        stopFollow(false);
      }
    } else if (type === 'near') {
      follow.near = (payload as { label: string }).label;
    }
  }

  // stopFollow leaves the session; leave is false when it already ended.
  // stopFollow stops following and returns once the server knows, so
  // following again right after it wakes the phone again.
  function stopFollow(leave = true): Promise<void> {
    if (rejoinTimer) clearInterval(rejoinTimer);
    rejoinTimer = null;
    unsubscribeLive?.();
    unsubscribeLive = null;
    const left = follow && leave ? api.delete(`/live/${follow.sessionId}/viewers/me`).then(() => {}, () => {}) : Promise.resolve();
    follow = null;
    (map?.getSource('trail') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: [] });
    return left;
  }

  function loadPlaces(groupId: string) {
    api.get<Place[]>(`/groups/${groupId}/places`)
      .then((list) => { allPlaces = [...allPlaces.filter((p) => p.group_id !== groupId), ...list]; render(); })
      .catch((e: any) => (loadError = e.message));
  }

  // LiveRoute is the route so far of an activity being recorded, split at
  // pauses and gaps as the server splits it; a gap is drawn as a dashed
  // straight line (spec 006 spec edge cases).
  type LiveRoute = { segs: { gap: boolean; pts: number[][] }[] };

  // routes holds the route so far of each member recording an activity, and
  // routeIds which activity each belongs to.
  let routes: Record<string, LiveRoute> = {};
  let routeIds: Record<string, string> = {};

  // loadRoute reads the route so far of a member's activity from the server,
  // which has every fix of each upload, as the apps do.
  // routeAsks numbers each member's route requests, so an older answer that
  // comes back last does not replace a newer route.
  const routeAsks: Record<string, number> = {};

  function loadRoute(userId: string, activityId: string) {
    const ask = (routeAsks[userId] = (routeAsks[userId] ?? 0) + 1);
    api.get<Activity>(`/groups/${groupOf(userId)}/activities/${activityId}?route=true`)
      .then((full) => {
        if (routeIds[userId] !== activityId || routeAsks[userId] !== ask) return;
        routes[userId] = { segs: (full.route ?? []).map((s) => ({ gap: !!s.gap_before, pts: s.points.map((p) => [p.lon, p.lat]) })) };
        render();
      })
      .catch(() => {});
  }

  // syncRoutes fetches the route so far of a member who started recording
  // and forgets that of one who stopped (spec 006 FR-030).
  function syncRoutes() {
    for (const m of everyone) {
      const a = m.activity;
      // A timer-only sport has no route to draw (spec 006).
      if (!a || !sportOf(a.type).gps) {
        delete routes[m.user_id];
        delete routeIds[m.user_id];
        continue;
      }
      if (routeIds[m.user_id] === a.id) continue;
      routeIds[m.user_id] = a.id;
      routes[m.user_id] = { segs: [] };
      loadRoute(m.user_id, a.id);
    }
  }

  // liveTrail is the followed member's line since following began, joined to
  // the end of their day's route as drawn: points the day already covers are
  // left out, so a reloaded day and the live points do not overlap
  // (002 FR-019).
  function liveTrail(): [number, number][] {
    if (!follow) return [];
    let end: { at: number; lon: number; lat: number } | undefined;
    if (focused === follow.userId) {
      for (const c of dayCards) {
        if (c.kind !== 'trip' || c.trip.gap || c.trip.route.length < 2) continue;
        const p = c.trip.route[c.trip.route.length - 1];
        const at = Date.parse(p.at);
        if (!end || at > end.at) end = { at, lon: p.lon, lat: p.lat };
      }
    }
    const after = end ? follow.trail.filter((p) => p.at > end!.at) : follow.trail;
    const pts = end && after.length > 0 ? [end, ...after] : after;
    return pts.map((p) => [p.lon, p.lat]);
  }

  // render draws a marker per located member shown, their accuracy circles, and the routes of activities being recorded.
  function render() {
    if (!map || !mapReady) return;
    const lines: number[][][] = [];
    const gaps: number[][][] = [];
    for (const r of Object.values(routes)) {
      r.segs.forEach((seg, i) => {
        if (seg.pts.length > 1) lines.push(seg.pts);
        const prev = r.segs[i - 1]?.pts.at(-1);
        if (seg.gap && prev && seg.pts.length > 0) gaps.push([prev, seg.pts[0]]);
      });
    }
    const paths = (cs: number[][][]): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: cs.map((c) => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: c } })),
    });
    (map.getSource('activities') as maplibregl.GeoJSONSource | undefined)?.setData(paths(lines));
    (map.getSource('activity-gaps') as maplibregl.GeoJSONSource | undefined)?.setData(paths(gaps));
    const trail = liveTrail();
    (map.getSource('trail') as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: trail.length > 1 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: trail } }] : [],
    });
    const seen = new Set<string>();
    const accuracy: GeoJSON.Feature[] = [];
    for (const m of members) {
      if (!m.location || m.status === 'paused') continue;
      seen.add(m.user_id);
      const { lat, lon, accuracy_m } = m.location;
      accuracy.push({ type: 'Feature', properties: {}, geometry: circlePolygon(lat, lon, accuracy_m) });
      let marker = markers.get(m.user_id);
      if (!marker) {
        const el = document.createElement('div');
        el.className = 'pin';
        el.innerHTML = '<button class="pin-head" type="button"></button><span class="pin-tail"></span><span class="pin-bubble" aria-hidden="true"></span>';
        el.querySelector('.pin-head')!.addEventListener('click', () => focus(m.user_id));
        marker = new maplibregl.Marker({ element: el, anchor: 'bottom-left', offset: [PIN_LEFT, 0] }).setLngLat([lon, lat]).addTo(map);
        markers.set(m.user_id, marker);
      }
      marker.setLngLat([lon, lat]);
      drawPin(marker.getElement(), m, stateOf(m));
    }
    for (const [id, marker] of markers) {
      if (!seen.has(id)) {
        marker.remove();
        markers.delete(id);
      }
    }
    (map.getSource('accuracy') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: accuracy });
    drawDestinations();
    joinUp();
    if (!fitted && seen.size > 0) {
      fitted = true;
      fitAll();
    }
  }

  // drawDestinations draws the route and a flag for each ETA a pin shows.
  function drawDestinations() {
    if (!map) return;
    const lines: GeoJSON.Feature[] = [];
    const keep = new Set<string>();
    for (const m of members) {
      const d = m.location && m.status !== 'paused' ? shownDestination(m) : undefined;
      if (!d) continue;
      keep.add(d.id);
      if (d.eta && d.eta.route.length > 1) lines.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: d.eta.route } });
      let flag = flags.get(d.id);
      if (!flag) {
        const el = document.createElement('div');
        el.className = 'eta-flag';
        el.innerHTML = '<i aria-hidden="true" class="las la-flag-checkered"></i>';
        flag = new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([d.lon, d.lat]).addTo(map);
        flags.set(d.id, flag);
      }
      flag.setLngLat([d.lon, d.lat]);
      const first = m.display_name.split(' ')[0];
      // 'your location' is the name the viewer's own spot is sent with.
      flag.getElement().title = d.name === 'your location' ? t('mapFlagYou', { member: first }) : t('mapFlagPlace', { member: first, place: d.name });
    }
    for (const [id, flag] of flags) {
      if (!keep.has(id)) {
        flag.remove();
        flags.delete(id);
      }
    }
    (map.getSource('eta-routes') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: lines });
  }

  // joinUp joins members whose pins would overlap on screen into one pin at
  // the middle of their spots, as the apps do (spec 010 FR-004), and
  // shows the rest on their own. The selected member keeps their own pin.
  function joinUp() {
    if (!map) return;
    const shown = members.filter((m) => markers.has(m.user_id)).sort((a, b) => (a.user_id < b.user_id ? -1 : 1));
    const spots = shown.map((m) => {
      const at = map!.project(markers.get(m.user_id)!.getLngLat());
      return { id: m.user_id, lat: m.location!.lat, lon: m.location!.lon, x: at.x, y: at.y };
    });
    const keep = new Set<string>();
    for (const j of joinPins(spots, PIN_SIZE, focused)) {
      if (j.ids.length === 1) {
        const marker = markers.get(j.ids[0])!;
        marker.getElement().style.display = '';
        marker.setOffset([j.ids[0] === focused ? PIN_LEFT_SELECTED : PIN_LEFT, 0]);
        continue;
      }
      for (const id of j.ids) markers.get(id)!.getElement().style.display = 'none';
      const key = j.ids.join(',');
      keep.add(key);
      let marker = joined.get(key);
      if (!marker) {
        const el = document.createElement('div');
        el.className = 'pin joined';
        el.innerHTML = '<button class="pin-stack" type="button"></button><span class="pin-tail"></span><div class="pin-choices" hidden></div>';
        el.querySelector('.pin-stack')!.addEventListener('click', (e) => {
          e.stopPropagation();
          const list = el.querySelector('.pin-choices') as HTMLElement;
          const open = list.hidden;
          closeChoices();
          // The list opens toward the larger part of the map, so the map's
          // edge does not cut it off.
          const box = map!.getContainer().getBoundingClientRect();
          list.classList.toggle('up', el.getBoundingClientRect().bottom > box.top + box.height / 2);
          list.hidden = !open;
        });
        marker = new maplibregl.Marker({ element: el, anchor: 'bottom-left' }).setLngLat([j.lon, j.lat]).addTo(map);
        joined.set(key, marker);
      }
      marker.setLngLat([j.lon, j.lat]);
      const inPin = j.ids.map((id) => shown.find((m) => m.user_id === id)!);
      drawJoined(marker.getElement(), inPin);
      marker.setOffset([-stackWidth(inPin.length) / 2, 0]);
    }
    for (const [key, marker] of joined) {
      if (!keep.has(key)) {
        marker.remove();
        joined.delete(key);
      }
    }
  }

  // stackWidth is how wide a joined pin's photos are for count members; its
  // tail's tip is at the middle.
  const stackWidth = (count: number) => STACK_PHOTO + STACK_STEP * (Math.min(count, STACK_MAX) - 1);

  // closeChoices closes any joined pin's list of members.
  function closeChoices() {
    for (const marker of joined.values()) (marker.getElement().querySelector('.pin-choices') as HTMLElement).hidden = true;
  }

  // drawJoined fills a joined pin (010 FR-004): up to three photos stacked, the
  // third saying "+N" when there are more, and the list of them that
  // clicking it opens. It is dimmed only when every member's
  // location is out of date.
  function drawJoined(el: HTMLElement, inPin: MemberLocation[]) {
    const states = inPin.map((m) => stateOf(m));
    el.classList.toggle('dim', states.every((st) => !st.current && st.kind !== 'MANUAL'));
    el.style.setProperty('--stack', `${stackWidth(inPin.length)}px`);
    const names = inPin.map((m) => m.display_name.split(' ')[0]).join(', ');
    const stack = el.querySelector('.pin-stack') as HTMLElement;
    stack.setAttribute('aria-label', t('mapJoinedPin', { n: inPin.length, names }));
    stack.title = stack.getAttribute('aria-label')!;
    const key = inPin.map((m) => `${m.user_id}|${m.photo ?? ''}|${m.color}`).join(';');
    if (stack.dataset.key !== key) {
      stack.dataset.key = key;
      stack.innerHTML = '';
      const photos = inPin.length > STACK_MAX ? inPin.slice(0, STACK_MAX - 1) : inPin;
      photos.forEach((m, i) => {
        const face = document.createElement('span');
        face.className = 'pin-face';
        face.style.left = `${i * STACK_STEP}px`;
        face.style.setProperty('--ring', m.color);
        face.textContent = initials(m.display_name);
        if (m.photo) photoUrl(m.user_id, m.photo).then((u) => { if (u && stack.dataset.key === key) { face.textContent = ''; face.style.backgroundImage = `url(${u})`; } });
        stack.append(face);
      });
      if (inPin.length > STACK_MAX) {
        const more = document.createElement('span');
        more.className = 'pin-face pin-more';
        more.style.left = `${(STACK_MAX - 1) * STACK_STEP}px`;
        more.textContent = t('mapJoinedMore', { n: integer(inPin.length - photos.length) });
        stack.append(more);
      }
    }
    const list = el.querySelector('.pin-choices') as HTMLElement;
    list.innerHTML = '';
    inPin.forEach((m, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pin-choice';
      const name = document.createElement('strong');
      name.textContent = m.display_name;
      const said = document.createElement('span');
      said.textContent = states[i].headline;
      b.append(name, said);
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        list.hidden = true;
        focus(m.user_id);
      });
      list.append(b);
    });
  }

  // drawPin fills a member's pin (010 FR-003, 010 FR-005): their photo or initials
  // in a ring of their color, dimmed when out of date, with a low battery
  // chip and a bubble for how long they have been at their place, their
  // speed while moving, or how old an out-of-date location is. The bubble
  // shows only while the member is selected.
  // namesPlace says whether a member's bubble names the place they are at.
  const namesPlace = (m: MemberLocation, st: MemberState): m is MemberLocation & { since: string } =>
    (st.kind === 'AT_PLACE' || st.kind === 'NEAR') && !!m.since;

  function drawPin(el: HTMLElement, m: MemberLocation, st: MemberState) {
    const dim = !st.current && st.kind !== 'MANUAL';
    el.classList.toggle('dim', dim);
    el.classList.toggle('selected', focused === m.user_id);
    el.style.setProperty('--ring', m.color);
    const head = el.querySelector('.pin-head') as HTMLElement;
    head.title = st.lowBattery ? t('mapPinTitleLowBattery', { member: m.display_name, state: st.headline }) : t('mapPinTitle', { member: m.display_name, state: st.headline });
    head.setAttribute('aria-label', head.title);
    const key = `${m.photo ?? ''}|${m.display_name}|${m.color}`;
    if (head.dataset.key !== key) {
      head.dataset.key = key;
      head.textContent = initials(m.display_name);
      head.style.backgroundImage = '';
      if (m.photo) photoUrl(m.user_id, m.photo).then((u) => { if (u && head.dataset.key === key) { head.textContent = ''; head.style.backgroundImage = `url(${u})`; } });
    }
    head.querySelector('.pin-battery')?.remove();
    if (st.lowBattery && m.location?.battery_pct != null) {
      const b = document.createElement('span');
      b.className = 'pin-battery';
      b.textContent = t('mapBatteryPercent', { percent: integer(m.location.battery_pct) });
      head.append(b);
    }
    const bubble = el.querySelector('.pin-bubble') as HTMLElement;
    let text = '';
    let icon = st.icon;
    // An ETA the viewer or the member set says only the speed and time left
    // (spec 003 FR-023); without one, a moving member's bubble says their
    // speed or what they are doing, as the apps' does.
    const eta = pinText(m, now, miles);
    if (eta) { text = eta; icon = 'flag'; }
    else if (st.kind === 'MOVING') text = st.speed ?? doing(m.location?.activity);
    else if (namesPlace(m, st)) text = m.place != null ? t('mapBubbleAtPlace', { place: m.place, duration: stayed(m.since) }) : t('mapBubbleHere', { duration: stayed(m.since) });
    else if (st.kind === 'PROBLEM' && m.location) { text = age(m.location.recorded_at, now); icon = 'exclamation'; }
    else if (st.kind === 'MANUAL' && m.location) text = age(m.location.recorded_at, now);
    bubble.hidden = !text || focused !== m.user_id;
    bubble.dataset.kind = eta ? 'ETA' : st.kind;
    bubble.innerHTML = '';
    if (text) {
      const i = document.createElement('i');
      i.setAttribute('aria-hidden', 'true');
      i.className = iconClass(icon);
      bubble.append(i, text);
    }
  }

  // stayed says how long since a time: 25 min, 3 h 9 min, 2 days.
  function stayed(iso: string): string {
    const mins = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
    if (mins < 60) return t('mapDurationMinutes', { n: mins });
    if (mins < 24 * 60) return mins % 60 ? t('mapDurationHoursMinutes', { hours: integer(Math.floor(mins / 60)), minutes: integer(mins % 60) }) : t('mapDurationHours', { n: mins / 60 });
    return t('mapDurationDays', { n: mins < 48 * 60 ? 1 : Math.floor(mins / (24 * 60)) });
  }

  // drawDay draws the selected member's trips today, the picked one, and
  // where they left their last stop.
  function drawDay() {
    if (!map || !mapReady) return;
    const line = (pts: { lat: number; lon: number }[]): GeoJSON.Feature => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: pts.map((p) => [p.lon, p.lat]) } });
    const trips = focused ? dayCards.flatMap((c) => (c.kind === 'trip' && !c.trip.gap && c.trip.route.length > 1 ? [c.trip] : [])) : [];
    (map.getSource('day') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: trips.map((trip) => line(trip.route)) });
    const chosen = picked?.kind === 'trip' && picked.trip.route.length > 1 ? [line(picked.trip.route)] : [];
    (map.getSource('picked') as maplibregl.GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: chosen });
    leftLabel?.remove();
    leftLabel = null;
    const last = focused ? dayCards.find((c) => c.kind === 'trip') : undefined;
    if (last && last.kind === 'trip' && last.trip.route.length > 0) {
      const el = document.createElement('div');
      el.className = 'map-note';
      el.textContent = last.from ? t('mapLeftPlace', { place: last.from, time: clock(last.trip.start) }) : t('mapLeftStop', { time: clock(last.trip.start) });
      leftLabel = new maplibregl.Marker({ element: el, anchor: 'bottom', offset: [0, -8] }).setLngLat([last.trip.route[0].lon, last.trip.route[0].lat]).addTo(map);
    }
  }

  function pick(card: DayCard) {
    picked = card;
    drawDay();
    if (!map) return;
    centering = false;
    if (card.kind === 'stay') {
      map.easeTo({ center: [card.stay.lon, card.stay.lat], zoom: Math.max(map.getZoom(), 15) });
    } else if (card.trip.route.length > 0) {
      const b = new maplibregl.LngLatBounds();
      for (const p of card.trip.route) b.extend([p.lon, p.lat]);
      map.fitBounds(b, { padding: 80, maxZoom: 16 });
    }
  }

  // fitAll frames those shown so each whole pin and its bubble shows, at the
  // closest zoom that allows it, as the apps do (spec 010 FR-005).
  function fitAll() {
    const located = members.filter((m) => m.location && m.status !== 'paused');
    if (!map || located.length === 0) return;
    const pins = located.map((m) => {
      // A marker sits with its spot a half pin in from its left edge and at
      // its bottom; its bubble may reach past its box.
      // A member inside a joined pin has their own marker hidden; they count
      // as a bare pin.
      const el = markers.get(m.user_id)?.getElement();
      let left = -PIN_LEFT, right = PIN_SIZE + PIN_LEFT, up = PIN_SIZE + 12;
      if (el && el.style.display !== 'none') {
        // The marker's own box spans the map's width, so its visible parts,
        // the head and the bubble, are measured instead.
        const box = el.getBoundingClientRect();
        const x = box.left - PIN_LEFT, y = box.bottom;
        let r = x + PIN_SIZE + PIN_LEFT, t = y - PIN_SIZE;
        for (const part of el.querySelectorAll<HTMLElement>('.pin-head, .pin-bubble')) {
          if (part.hidden) continue;
          const b = part.getBoundingClientRect();
          r = Math.max(r, b.right);
          t = Math.min(t, b.top);
        }
        right = r - x;
        up = y - t;
      }
      return { lat: m.location!.lat, lon: m.location!.lon, left: left + 8, right: right + 8, up: up + 8 };
    });
    const c = map.getContainer();
    const f = fitPins(pins, c.clientWidth, c.clientHeight, { top: 70, right: 60, bottom: 30, left: 20 }, 15);
    if (f) map.easeTo({ center: [f.lon, f.lat], zoom: f.zoom });
  }

  // showAll fits everyone the filter shows, and lets go of a followed member
  // so their next point does not pull the map back (spec 010 FR-008).
  function showAll() {
    centering = false;
    fitAll();
  }

  // showMe centers the map on the viewer's last location.
  function showMe() {
    if (!map || !own) return;
    centering = false;
    map.flyTo({ center: [own.lon, own.lat], zoom: Math.max(map.getZoom(), 15) });
  }

  // recording reports whether a member is recording a GPS activity now,
  // which selecting them watches every 10 seconds (spec 006).
  // A timer-only one takes no extra locations (spec 006 FR-008).
  function recording(m: MemberLocation): boolean {
    return m.activity?.state === 'recording' && sportOf(m.activity.type).gps;
  }

  function focus(userId: string) {
    if (follow && follow.userId !== userId) stopFollow();
    if (focused !== userId) {
      dayCards = [];
      picked = null;
    }
    focused = userId;
    centering = true;
    render();
    const m = members.find((x) => x.user_id === userId);
    if (map && m?.location) map.flyTo({ center: [m.location.lon, m.location.lat], zoom: Math.max(map.getZoom(), 15) });
    // Selecting a member follows them live (002 FR-014), which the effect below
    // starts; selecting them again follows again after a follow ended.
    watchedFor = '';
  }

  // unfocus closes the member view, which stops following them.
  function unfocus() {
    stopFollow();
    focused = null;
    dayCards = [];
    picked = null;
    drawDay();
    render();
    fitAll();
  }

  // The selected member is followed live, unless they share only when they
  // choose: once per selection and activity, so a refused follow is not
  // asked again on every update.
  let watchedFor = '';
  $effect(() => {
    const m = members.find((x) => x.user_id === focused);
    // Recording is a manual member choosing to share (spec 006 FR-030).
    // The viewer's own entry is their own phone; there is no one to follow.
    if (!m || m.user_id === viewerId || (m.tracking_mode === 'manual' && !recording(m)) || follow || awayFrom) return;
    const key = m.user_id + (m.activity?.id ?? '');
    if (watchedFor === key) return;
    watchedFor = key;
    untrack(() => startFollow(m.user_id));
  });

  // Pins and ages redraw as time passes and when the units change.
  $effect(() => {
    void now;
    void miles;
    untrack(() => render());
  });

  const viewerId = $derived($auth.user?.id ?? '');
  // own is the viewer's last location, which their phone reports.
  const own = $derived(everyone.find((m) => m.user_id === viewerId)?.location);
  const ordered = $derived([...members].sort((a, b) => Number(a.user_id !== viewerId) - Number(b.user_id !== viewerId) || a.display_name.localeCompare(b.display_name)));
  const trouble = $derived(members.filter((m) => stateOf(m).kind === 'PROBLEM').length);

  // noGroup is the no-group sentence split around its link.
  const noGroup = t('mapNoGroup', { create: '\u0000' }).split('\u0000');

  // activityLine is a recording member's activity: its name, time, distance
  // and pace when it has a route, and how old its last update is.
  function activityLine(a: LiveActivity): string {
    const gps = sportOf(a.type).gps;
    const pace = gps ? rate(a.type, a.distance_m, a.moving_s ?? 0) : '';
    const parts = [a.state === 'paused' ? t('mapActivityPaused', { activity: activityLabel(a.type) }) : activityLabel(a.type), clockTime(a.elapsed_s)];
    if (gps) parts.push(length(a.distance_m));
    if (pace) parts.push(pace);
    const line = parts.join(' · ');
    return a.progress_at ? t('mapActivityAge', { activity: line, age: age(a.progress_at, now) }) : line;
  }

  function stateColor(st: MemberState): string {
    if (st.kind === 'PROBLEM') return 'var(--theme-error)';
    if (st.kind === 'PAUSED' || st.kind === 'MANUAL') return 'var(--theme-warning)';
    if (st.kind === 'AT_PLACE') return 'var(--theme-success)';
    if (st.kind === 'MOVING') return 'var(--theme-accent)';
    return 'var(--theme-subtext0)';
  }
</script>

<!-- On a narrow screen, or zoomed in, the map sits above the list instead of beside it. -->
<div class="flex flex-col-reverse md:flex-row flex-1 min-h-0">
  <aside class="w-full md:w-80 h-1/2 md:h-auto shrink-0 border-t md:border-t-0 md:border-r flex flex-col min-h-0" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <div class="p-4 border-b" style="border-color: var(--theme-surface0);">
      {#if $groups.length > 0}
        <label for="group" class="block text-xs text-text-muted mb-1">{t('mapShowing')}</label>
        <select id="group" class="input w-full" bind:value={filter}>
          <option value="">{t('mapAllGroups')}</option>
          {#each $groups as c (c.id)}
            <option value={c.id}>{groupLabel(c, $groups)}</option>
          {/each}
        </select>
      {:else if !loadError}
        <p class="text-sm text-text-muted">{noGroup[0]}<Link to="/groups">{t('mapNoGroupCreate')}</Link>{noGroup[1]}</p>
      {/if}
      {#if loadError}
        <p class="text-sm mt-2" style="color: var(--theme-error);">{loadError}</p>
      {/if}
    </div>

    {#if focused && members.find((x) => x.user_id === focused) && groupOf(focused)}
      {@const m = members.find((x) => x.user_id === focused)!}
      {@const self = everyone.find((x) => x.user_id === viewerId)?.location}
      <MemberPanel
        groupId={groupOf(m.user_id)}
        {viewerId}
        member={m}
        st={stateOf(follow && follow.userId === m.user_id && follow.near ? { ...m, near: follow.near } : m)}
        viewer={self ? { lat: self.lat, lon: self.lon } : undefined}
        places={allPlaces}
        live={follow && follow.userId === m.user_id ? { state: follow.state, reason: follow.reason } : undefined}
        {followError}
        {now}
        picked={picked?.start ?? null}
        onClose={unfocus}
        onFollowAgain={() => startFollow(m.user_id)}
        onDay={(cards) => { dayCards = cards; untrack(() => { drawDay(); render(); }); }}
        onPick={pick}
      />
    {:else}
      {#if members.length > 0}
        <p class="px-4 pt-3 text-xs" style="color: {trouble > 0 ? 'var(--theme-error)' : 'var(--theme-subtext0)'};">
          {trouble > 0 ? t('mapMemberCountTrouble', { n: members.length, trouble }) : t('mapMemberCount', { n: members.length })}
        </p>
      {/if}
      <ul class="flex-1 overflow-y-auto py-1">
        {#each ordered as m (m.user_id)}
          {@const st = stateOf(m)}
          <li>
            <button class="member-row" onclick={() => focus(m.user_id)}>
              <span class="flex-1 min-w-0">
                <span class="block font-semibold truncate">{m.display_name}</span>
                <span class="block text-sm break-words" style="color: {st.kind === 'PROBLEM' || st.kind === 'PAUSED' || st.kind === 'MANUAL' ? stateColor(st) : 'var(--theme-text)'};">
                  <i aria-hidden="true" class="{iconClass(st.icon)} mr-1" style="color: {stateColor(st)};"></i>{st.headline}
                </span>
                {#if st.detail}<span class="block text-xs text-text-muted">{st.detail}</span>{/if}
                {#if m.activity}
                  <span class="block text-xs mt-0.5" style="color: var(--theme-accent);">
                    <i aria-hidden="true" class="las {activityIcon(m.activity.type)}"></i>
                    {activityLine(m.activity)}
                  </span>
                {/if}
                {#each st.notes as n (n.text)}
                  <span class="block text-xs mt-0.5" style="color: {n.severe ? 'var(--theme-error)' : 'var(--theme-subtext0)'};">
                    <i aria-hidden="true" class="las {n.severe ? 'la-exclamation-circle' : 'la-car-crash'} mr-1"></i>{n.text}
                  </span>
                {/each}
              </span>
              <!-- The photo comes last in reading order, so a screen reader says the name first and the battery last. -->
              <span class="order-first flex shrink-0">
                <Avatar name={m.display_name} userId={m.user_id} color={m.color} photo={m.photo} size={48} dot={st.current}
                        dim={!st.current && st.kind !== 'MANUAL'} battery={m.location?.battery_pct} charging={m.location?.charging ?? false} />
              </span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </aside>

  <div class="flex-1 relative min-w-0">
    <div bind:this={mapEl} class="absolute inset-0"></div>
    <div bind:this={viewEl} class="maplibregl-ctrl maplibregl-ctrl-group view-buttons">
      <button type="button" title={t('mapCenterOnYou')} aria-label={t('mapCenterOnYou')} disabled={!own} onclick={showMe}>
        <i aria-hidden="true" class="las la-crosshairs"></i>
      </button>
      <button type="button" title={t('mapShowEveryone')} aria-label={t('mapShowEveryone')} disabled={!members.some((m) => m.location && m.status !== 'paused')} onclick={showAll}>
        <i aria-hidden="true" class="las la-expand-arrows-alt"></i>
      </button>
    </div>
    <div class="absolute top-3 left-3 right-16 z-10 space-y-2">
      {#each sosPaged as a (a.id)}
        <Link to={`/sos/${a.id}`} class="card block p-3 text-sm font-semibold" style="color: var(--theme-error);">
          <i aria-hidden="true" class="las la-exclamation-triangle mr-1"></i>{t('mapSosPaged', { member: a.display_name })}
        </Link>
      {/each}
      {#if sosMine}
        <Link to={`/sos/${sosMine.id}`} class="card block p-3 text-sm font-semibold" style="color: var(--theme-error);">
          <i aria-hidden="true" class="las la-exclamation-triangle mr-1"></i>{t('mapSosMine', { recipients: sosMine.recipients?.map((r) => r.display_name).join(', ') ?? '' })}
        </Link>
      {/if}
      {#each crashAlerts as a (a.ref)}
        <Link to={`/crash/${a.ref}`} class="card block p-3 text-sm font-semibold" style="color: var(--theme-error);">
          <i aria-hidden="true" class="las la-car-crash mr-1"></i>{t('mapCrashBanner', { title: a.title })}
        </Link>
      {/each}
      {#each watching as c (c.id)}
        <Link to={`/check-in/${c.id}`} class="card block p-3 text-sm {c.state === 'alerted' ? 'font-semibold' : ''}" style={c.state === 'alerted' ? 'color: var(--theme-error);' : ''}>
          <i aria-hidden="true" class="las la-stopwatch mr-1"></i>{c.state === 'alerted' ? t('mapCheckInAlerted', { member: c.display_name }) : c.note && c.state === 'running' ? t('mapCheckInDueNote', { member: c.display_name, when: clockAt(c.due_at), note: c.note }) : t('mapCheckInDue', { member: c.display_name, when: clockAt(c.due_at) })}
        </Link>
      {/each}
      {#if mine}
        <Link to="/check-in" class="card block p-3 text-sm" style={mine.state === 'alerted' ? 'color: var(--theme-error);' : ''}>
          <i aria-hidden="true" class="las la-stopwatch mr-1"></i>{mine.state === 'alerted' ? t('mapMyCheckInPassed') : t('mapMyCheckInDue', { when: clockAt(mine.due_at) })}
        </Link>
      {/if}
      {#if shareLinks.length > 0}
        {@const only = shareLinks.length === 1 ? shareLinks[0] : null}
        <div class="card flex items-center gap-2 p-3 text-sm">
          <button class="min-w-0 flex-1 text-left" onclick={() => (shareOpen = true)}>
            <i aria-hidden="true" class="las la-link mr-1"></i>{only
              ? only.place_name
                ? t('mapShareLinkUntilPlace', { place: only.place_name })
                : t('mapShareLinkUntil', { when: clockAt(only.ends_at) })
              : t('mapShareLinksCount', { n: shareLinks.length })}
          </button>
          {#if only}
            <button class="btn btn-secondary btn-sm" aria-label={t('mapShareLinkStopLabel')} onclick={() => stopShareLink(only)}>{t('mapShareLinkStop')}</button>
          {/if}
        </div>
      {/if}
    </div>
    <div class="absolute bottom-8 left-3 z-10 flex items-end gap-3">
      <SosButton onsent={(a) => navigate(`/sos/${a.id}`)} />
      <ShareLinks bind:open={shareOpen} onchange={loadShareLinks} />
    </div>
    {#if mapError}
      <div class="absolute top-3 left-3 right-16 card p-3 text-sm" style="color: var(--theme-warning);">{mapError}</div>
    {:else if refreshNote}
      <div class="absolute top-3 left-3 card px-3 py-2 text-sm"><i aria-hidden="true" class="las la-sync mr-1"></i>{refreshNote}</div>
    {/if}
  </div>
</div>

<style>
  /* MapLibre's control buttons are white in either theme. */
  .view-buttons button {
    color: #333;
    font-size: 1.15rem;
  }
  .view-buttons button:disabled {
    color: #aaa;
  }

  .member-row {
    width: 100%;
    text-align: left;
    padding: 0.75rem 1rem;
    display: flex;
    gap: 0.9rem;
    align-items: center;
  }
  .member-row:hover {
    background: var(--theme-surface0);
  }

  /* A pin's tail tip sits on the member's spot: MapLibre anchors the
     marker's bottom left, moved left by half the pin. */
  :global(.pin) {
    --ring: var(--theme-accent);
    display: flex;
    align-items: flex-start;
    pointer-events: none;
  }
  :global(.pin-head) {
    pointer-events: auto;
    position: relative;
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
    border: 3px solid var(--ring);
    background-color: var(--ring);
    background-size: cover;
    background-position: center;
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--theme-shadow-md);
    cursor: pointer;
  }
  /* The selected member's pin draws above the others, as in the apps. */
  :global(.pin.selected) {
    z-index: 2;
  }
  :global(.pin.selected .pin-head) {
    width: 3.5rem;
    height: 3.5rem;
  }
  :global(.pin-tail) {
    position: absolute;
    left: 1.5rem;
    bottom: 0;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 9px solid var(--ring);
  }
  :global(.pin.selected .pin-tail) {
    left: 1.75rem;
  }
  /* MapLibre places a marker absolutely, which also holds the tail; a pin
     must not override that, or markers fall into the page's flow and each
     sits below the ones before it. */
  :global(.pin) {
    padding-bottom: 9px;
  }
  :global(.pin.dim .pin-head),
  :global(.pin.dim .pin-tail) {
    opacity: 0.55;
  }
  /* A joined pin's photos overlap left to right, its tail under their middle. */
  :global(.pin-stack) {
    pointer-events: auto;
    position: relative;
    width: var(--stack);
    height: 40px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }
  :global(.pin-face) {
    position: absolute;
    top: 0;
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    border: 2px solid var(--ring, var(--theme-accent));
    background-color: var(--ring, var(--theme-accent));
    background-size: cover;
    background-position: center;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--theme-shadow-md);
  }
  :global(.pin-more) {
    background-color: var(--theme-base);
    border-color: var(--theme-accent);
    color: var(--theme-text);
  }
  :global(.pin.joined .pin-tail) {
    left: calc(var(--stack) / 2);
    border-top-color: var(--theme-accent);
  }
  :global(.pin.joined.dim .pin-stack),
  :global(.pin.joined.dim .pin-tail) {
    opacity: 0.55;
  }
  :global(.pin-choices) {
    pointer-events: auto;
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 5;
    min-width: 12rem;
    display: flex;
    flex-direction: column;
    padding: 0.25rem;
    border-radius: 0.75rem;
    background: var(--theme-base);
    border: 1px solid var(--theme-border);
    box-shadow: var(--theme-shadow-md);
  }
  :global(.pin-choices.up) {
    top: auto;
    bottom: calc(100% + 4px);
  }
  :global(.pin-choices[hidden]) {
    display: none;
  }
  :global(.pin-choice) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.1rem;
    padding: 0.45rem 0.6rem;
    border-radius: 0.5rem;
    border: 0;
    background: none;
    color: var(--theme-text);
    text-align: left;
    cursor: pointer;
  }
  :global(.pin-choice span) {
    font-size: 0.8rem;
    color: var(--theme-text-muted);
  }
  :global(.pin-choice:hover) {
    background: var(--theme-surface0);
  }
  :global(.pin-battery) {
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 0 0.25rem;
    border-radius: 0.4rem;
    font-size: 0.6rem;
    background: var(--theme-base);
    color: var(--theme-error);
    border: 1px solid var(--theme-surface1);
  }
  :global(.pin-bubble) {
    margin: 0.4rem 0 0 0.3rem;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    max-width: 11rem;
    padding: 0.15rem 0.5rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: var(--theme-base);
    color: var(--theme-text);
    border: 1px solid var(--theme-surface1);
    box-shadow: var(--theme-shadow-sm);
  }
  :global(.pin-bubble[hidden]) {
    display: none;
  }
  :global(.pin-bubble i) {
    color: var(--theme-success);
  }
  :global(.pin-bubble[data-kind='MOVING'] i) {
    color: var(--theme-accent);
  }
  :global(.pin-bubble[data-kind='PROBLEM'] i) {
    color: var(--theme-error);
  }
  :global(.pin-bubble[data-kind='ETA'] i) {
    color: var(--theme-mauve, var(--theme-accent));
  }
  :global(.eta-flag) {
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 9999px 9999px 9999px 0;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--theme-base);
    border: 2px solid var(--theme-mauve, var(--theme-accent));
    box-shadow: var(--theme-shadow-sm);
  }
  :global(.eta-flag i) {
    transform: rotate(45deg);
    color: var(--theme-mauve, var(--theme-accent));
  }
  :global(.map-note) {
    padding: 0.15rem 0.5rem;
    border-radius: 0.75rem;
    font-size: 0.75rem;
    white-space: nowrap;
    background: var(--theme-base);
    color: var(--theme-text);
    border: 1px solid var(--theme-surface1);
  }
</style>
